import React from "react"
import cn from "classnames"
import usePreRender from "hooks/usePreRender"
import Swal from "sweetalert2"
import { CopyPlus, ChevronDown, ChevronRight, ChevronsUpDown, ChevronsDownUp, Trash2, Plus, Search } from "lucide-react"

const deleteDeepKey = (obj: any, path: string[]): any => {
  const newObj = JSON.parse(JSON.stringify(obj))
  let current = newObj
  for (let i = 0; i < path.length - 1; i++) {
    current = current[path[i]]
  }
  const lastKey = path[path.length - 1]
  if (Array.isArray(current)) {
    current.splice(Number(lastKey), 1)
  } else {
    delete current[lastKey]
  }
  return newObj
}

const setDeepValue = (obj: any, path: string[], value: any) => {
  const newObj = JSON.parse(JSON.stringify(obj))
  let current = newObj
  for (let i = 0; i < path.length - 1; i++) {
    current = current[path[i]]
  }
  current[path[path.length - 1]] = value
  return newObj
}


const iosSwalBase = {
  buttonsStyling: false,
  customClass: {
    popup: "!w-[280px] !rounded-[14px] !p-0 overflow-hidden shadow-2xl border border-gray-100",
    actions: "!mx-0 !w-full !border-t !border-gray-200/60 !p-0 !mt-0",
  },
}

const getIOSSwalConfig = (_colorClass: string) => ({
  ...iosSwalBase,
  customClass: {
    ...iosSwalBase.customClass,
    title: "!text-[17px] !font-semibold !text-black !pt-5 !px-4 !m-0",
    input: "!text-[14px] !rounded-lg !border !border-gray-300 !px-3 !py-2 !mt-3 !mb-4 !mx-4 !w-[calc(100%-32px)] focus:!border-blue-500 focus:!ring-1 focus:!ring-blue-500 outline-none",
    confirmButton: "!w-1/2 !m-0 !px-4 !py-3 !text-[17px] !font-semibold !bg-transparent !border-l !border-gray-200/60 !rounded-none hover:!bg-gray-50 !text-[#007AFF] !outline-none focus:!ring-0",
    cancelButton: "!w-1/2 !m-0 !px-4 !py-3 !text-[17px] !font-normal !bg-transparent !rounded-none hover:!bg-gray-50 !text-[#007AFF] !outline-none focus:!ring-0",
  },
})

const getIOSDeleteConfig = (title: string, text: string) => ({
  ...iosSwalBase,
  html: `
    <div class="flex flex-col items-center justify-center pt-5 pb-2 px-4">
      <div class="w-12 h-12 bg-red-50 rounded-full flex items-center justify-center mb-3">
        <svg class="w-6 h-6 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
        </svg>
      </div>
      <h3 class="text-[17px] font-semibold text-gray-900 tracking-tight mb-1">${title}</h3>
      <p class="text-[13px] text-gray-500 font-medium leading-snug text-center">${text}</p>
    </div>
  `,
  showCancelButton: true,
  confirmButtonText: "Eliminar",
  cancelButtonText: "Cancelar",
  customClass: {
    ...iosSwalBase.customClass,
    confirmButton: "!w-1/2 !m-0 !px-4 !py-3 !text-[17px] !font-semibold !bg-transparent !border-l !border-gray-200/60 !rounded-none hover:!bg-red-50 !text-[#F9004D] !outline-none focus:!ring-0",
    cancelButton: "!w-1/2 !m-0 !px-4 !py-3 !text-[17px] !font-normal !bg-transparent !rounded-none hover:!bg-gray-50 !text-[#007AFF] !outline-none focus:!ring-0",
  },
})

const FIELD_LABELS: Record<string, string> = {
  // General
  isEnabled:                    "Habilitado",
  isEnable:                     "Habilitado",
  enabled:                      "Habilitado",
  // Fechas
  startDate:                    "Fecha de inicio",
  endDate:                      "Fecha de término",
  // Costos
  cost:                         "Costo",
  fixedCost:                    "Costo fijo",
  valueTotal:                   "Valor total",
  valueNeto:                    "Valor neto",
  tax:                          "Impuesto",
  shippingCost:                 "Costo de envío",
  // Reglas de costo
  simCardRule:                  "Regla SIM Card",
  dateRules:                    "Reglas de fecha",
  deliveryTypeCosts:            "Costos por tipo de envío",
  channelCosts:                 "Costos por canal",
  cartValueRules:               "Reglas por valor del carrito",
  minCartValue:                 "Valor mínimo del carrito",
  // Brokering
  brokering:                    "Brokering",
  schedulesOrder:               "Orden de tipos de entrega",
  sortBySourceProximity:        "Ordenar por proximidad",
  sourceCoverageRatio:          "Ratio de cobertura",
  sortSourcesCoverageByRanking: "Ordenar cobertura por ranking",
  sortCombinationsBySourceRanking: "Ordenar combinaciones por ranking",
  rescheduleConfig:             "Config. de reagendamiento",
  shippingType:                 "Tipo de envío",
  deliveryTypes:                "Tipos de entrega",
  limitStorePickupSources:      "Límite fuentes de retiro",
  limitStorePickupSchedules:    "Límite horarios de retiro",
  sameDay:                      "Mismo día",
  nextDay:                      "Día siguiente",
  presaleSchedules:             "Horarios de preventa",
  // Integraciones
  fixedQueryItems:              "Items de consulta fijos",
  sourceCoverageRange:          "Rango de cobertura",
  // Core
  core:                         "Core",
  category:                     "Categoría",
  // Regiones / Canales
  regionMapping:                "Mapeo de regiones",
  channelCostMapping:           "Mapeo de costos por canal",
  // Cancelación
  cancelReasons:                "Razones de cancelación",
  createReversePickingOrder:    "Crear orden de devolución",
  friendlyName:                 "Nombre amigable",
  // Schema
  createOrderInputSchemaValidation: "Validación de esquema de entrada",
  // Misc
  range:                        "Rango",
  nodo:                         "Nodo",
}

const getLabel = (key: string): string => {
  const translation = FIELD_LABELS[key]
  if (translation) return `${translation} (${key})`
  return key
}

interface JsonFormNodeProps {

  dataKey: string
  value: any
  path: string[]
  onChange: (path: string[], val: any) => void
  onDelete?: () => void
  _color: string
  onClone?: () => void
  expandSignal?: number
  collapseSignal?: number
  readOnly?: boolean
}

const JsonFormNode: React.FC<JsonFormNodeProps> = ({ dataKey, value, path, onChange, onDelete, _color, onClone, expandSignal, collapseSignal, readOnly }) => {
  const type = typeof value
  const isArray = Array.isArray(value)

  const [isCollapsed, setIsCollapsed] = React.useState(false)

  React.useEffect(() => {
    if (expandSignal && expandSignal > 0) setIsCollapsed(false)
  }, [expandSignal])

  React.useEffect(() => {
    if (collapseSignal && collapseSignal > 0) setIsCollapsed(true)
  }, [collapseSignal])

  if (value === null) {
    return (
      <div className="mb-4">
        <div className="flex items-center justify-between mb-1">
          <label className="block text-sm font-semibold text-gray-700">{getLabel(dataKey)}</label>
          {!readOnly && onClone && (
            <button onClick={(e) => { e.preventDefault(); onClone(); }} title="Clonar propiedad" className="text-gray-400 hover:text-blue-600 transition-colors p-1 bg-white rounded shadow-sm border border-gray-200">
              <CopyPlus size={14} />
            </button>
          )}
        </div>
        <span className="text-sm px-3 py-1.5 bg-gray-50 border border-gray-200 rounded-md text-gray-400 italic inline-block">null</span>
      </div>
    )
  }

  if (type === "boolean") {
    return (
      <div className="mb-4 w-full">
        <div className="flex items-center justify-between mb-1.5">
          <label className="block text-sm font-semibold text-gray-700">{getLabel(dataKey)}</label>
          {!readOnly && onClone && (
            <button onClick={(e) => { e.preventDefault(); onClone(); }} title="Clonar propiedad" className="text-gray-400 hover:text-blue-600 transition-colors p-1 bg-white rounded shadow-sm border border-gray-200">
              <CopyPlus size={14} />
            </button>
          )}
        </div>
        <div className={cn("flex items-center p-3 rounded-xl shadow-sm transition-colors border", value ? `bg-green-50 border-green-200` : "bg-gray-50 border-gray-200", readOnly && "opacity-70")}>
          <label className={cn("relative inline-flex items-center", readOnly ? "cursor-default" : "cursor-pointer")}>
            <input
              type="checkbox"
              checked={value}
              disabled={readOnly}
              onChange={(e) => onChange(path, e.target.checked)}
              className="sr-only peer"
            />
            <div className={cn("w-11 h-6 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all shadow-inner", value ? "bg-green-500" : "bg-gray-300")}></div>
          </label>
          <span className={cn("ml-3 text-sm font-bold uppercase tracking-wide", value ? "text-green-600" : "text-gray-500")}>
            {value ? "Habilitado" : "Deshabilitado"}
          </span>
        </div>
      </div>
    )
  }

  if (type === "string" || type === "number") {
    const isDateField = type === "string" && (dataKey.toLowerCase().includes("date") || /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(value))
    
    let displayValue = value
    if (isDateField && typeof value === "string" && value) {
      const match = value.match(/^(\d{4}-\d{2}-\d{2}T\d{2}:\d{2})/)
      if (match) displayValue = match[1]
    }

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      let val: string | number = e.target.value
      if (type === "number") {
        val = Number(val)
      } else if (isDateField) {
        if (!val) {
          val = ""
        } else {
          const hasSeconds = (val as string).split(":").length === 3
          const isEnd = dataKey.toLowerCase().includes("end")
          if (!hasSeconds) {
            val = (val as string) + (isEnd ? ":59.999Z" : ":00.000Z")
          } else {
            val = (val as string) + (isEnd ? ".999Z" : ".000Z")
          }
        }
      }
      onChange(path, val)
    }

    return (
      <div className="mb-4 w-full">
        <div className="flex items-center justify-between mb-1.5">
          <label className="block text-sm font-semibold text-gray-700">{getLabel(dataKey)}</label>
          {!readOnly && onClone && (
            <button onClick={(e) => { e.preventDefault(); onClone(); }} title="Clonar propiedad" className="text-gray-400 hover:text-blue-600 transition-colors p-1 bg-white rounded shadow-sm border border-gray-200">
              <CopyPlus size={14} />
            </button>
          )}
        </div>
        <input
          type={isDateField ? "datetime-local" : type === "number" ? "number" : "text"}
          value={displayValue}
          disabled={readOnly}
          readOnly={readOnly}
          onChange={handleChange}
          className={cn("w-full px-4 py-2.5 bg-white border border-gray-200 rounded-xl shadow-sm text-sm text-gray-800 font-medium transition-all", readOnly ? "cursor-default text-gray-500 bg-gray-50" : `focus:outline-none focus:ring-2 focus:ring-opacity-40 focus:ring_${_color} focus:border_${_color}`)}
        />
      </div>
    )
  }

  if (type === "object") {
    // Special case: array of primitives (strings/numbers) → render as tag chips
    const isPrimitiveArray = isArray && value.length >= 0 && (value.length === 0 || value.every((v: any) => typeof v === "string" || typeof v === "number"))

    if (isPrimitiveArray) {
      return (
        <div className="mb-4 w-full min-w-0">
          <div className="flex items-center justify-between mb-1.5">
            <label className="block text-sm font-semibold text-gray-700">{getLabel(dataKey)}</label>
            {!readOnly && onClone && (
              <button onClick={(e) => { e.preventDefault(); onClone(); }} title="Clonar" className="text-gray-400 hover:text-blue-600 transition-colors p-1 bg-white rounded shadow-sm border border-gray-200">
                <CopyPlus size={14} />
              </button>
            )}
          </div>
          <div className="w-full p-3 bg-white border border-gray-200 rounded-xl shadow-sm flex flex-wrap gap-2">
            {value.map((item: any, idx: number) => (
              <div key={idx} className={cn("flex items-center gap-1 max-w-full rounded-full px-3 py-1 shadow-sm text-white", `bg_${_color}`)}>
                <span className="text-xs font-semibold break-all">{item}</span>
                {!readOnly && (
                  <button
                    onClick={() => {
                      const newArr = value.filter((_: any, i: number) => i !== idx)
                      onChange(path, newArr)
                    }}
                    className="text-gray-300 hover:text-red-500 transition-colors ml-0.5 shrink-0 leading-none"
                    title="Eliminar"
                  >
                    ×
                  </button>
                )}
              </div>
            ))}
            {!readOnly && (
              <button
                onClick={async () => {
                  const { value: newVal } = await Swal.fire({
                    title: "Nuevo valor",
                    input: "text",
                    inputPlaceholder: "Ingresa el valor",
                    showCancelButton: true,
                    confirmButtonText: "Añadir",
                    cancelButtonText: "Cancelar",
                    ...getIOSSwalConfig(_color)
                  })
                  if (newVal !== undefined && newVal !== "") {
                    onChange(path, [...value, newVal])
                  }
                }}
                className="flex items-center gap-1 text-xs font-semibold text-blue-500 hover:text-blue-700 border border-dashed border-blue-300 rounded-full px-3 py-1 transition-colors shrink-0"
              >
                + Añadir
              </button>
            )}
          </div>
        </div>
      )
    }

    // Special case: empty object {} → show placeholder
    if (!isArray && Object.keys(value).length === 0) {
      return (
        <div className="mb-4 w-full min-w-0">
          <div className="flex items-center justify-between mb-1.5">
            <label className="block text-sm font-semibold text-gray-700">{getLabel(dataKey)}</label>
            {!readOnly && onClone && (
              <button onClick={(e) => { e.preventDefault(); onClone(); }} title="Clonar" className="text-gray-400 hover:text-blue-600 transition-colors p-1 bg-white rounded shadow-sm border border-gray-200">
                <CopyPlus size={14} />
              </button>
            )}
          </div>
          <div className="px-4 py-3 bg-gray-50 border border-dashed border-gray-200 rounded-xl text-xs text-gray-400 italic flex items-center gap-2">
            <span className="text-base">∅</span> Objeto vacío
          </div>
        </div>
      )
    }
    return (
      <div className="mb-5 w-full min-w-0 overflow-hidden bg-white border border-gray-100 shadow-md rounded-2xl">
        <div className={cn("px-4 py-3 bg-gradient-to-r from-gray-50 to-white border-b border-gray-100 flex flex-wrap items-center justify-between gap-2")}>
          <div className="flex items-center gap-2 cursor-pointer select-none min-w-0 flex-1" onClick={() => setIsCollapsed(!isCollapsed)}>
            <div className="shrink-0">
              {isCollapsed ? <ChevronRight size={18} className="text-gray-400 hover:text-gray-700 transition-colors" /> : <ChevronDown size={18} className="text-gray-400 hover:text-gray-700 transition-colors" />}
            </div>
            <h4 className="text-sm font-bold text-gray-800 truncate tracking-tight">
              {getLabel(dataKey)}
            </h4>
          </div>
          {!readOnly && !isArray && (
            <button
              onClick={async (e) => {
                e.preventDefault()
                const res = await Swal.fire({
                  title: "Nueva propiedad",
                  input: "text",
                  inputPlaceholder: "nombreClave",
                  showCancelButton: true,
                  confirmButtonText: "Agregar",
                  cancelButtonText: "Cancelar",
                  ...getIOSSwalConfig(_color),
                  inputValidator: (v) => {
                    if (!v) return "Ingresa un nombre"
                    if (v in value) return "Esa clave ya existe"
                  }
                })
                if (res.value) {
                  onChange(path, { ...value, [res.value]: "" })
                }
              }}
              title="Agregar clave"
              className="text-gray-400 hover:text-green-600 transition-colors bg-white p-1.5 rounded shadow-sm border border-gray-200"
            >
              <Plus size={16} />
            </button>
          )}
          {!readOnly && onClone && (
            <button
              onClick={(e) => {
                e.preventDefault()
                onClone()
              }}
              title="Clonar bloque"
              className="text-gray-500 hover:text-blue-600 transition-colors bg-white p-1.5 rounded shadow-sm border border-gray-200"
            >
              <CopyPlus size={16} />
            </button>
          )}
          {!readOnly && onDelete && (
            <button
              onClick={async (e) => {
                e.preventDefault()
                const result = await Swal.fire(
                  getIOSDeleteConfig(
                    "¿Eliminar bloque?",
                    `Se eliminará "${dataKey}" y todo su contenido.`
                  )
                )
                if (result.isConfirmed) onDelete()
              }}
              title="Eliminar bloque"
              className="text-gray-400 hover:text-red-500 transition-colors bg-white p-1.5 rounded shadow-sm border border-gray-200"
            >
              <Trash2 size={16} />
            </button>
          )}
        </div>
        {!isCollapsed && (
          <div className={cn("p-5 flex flex-col gap-4 bg-gray-50/30")}>
            {Object.keys(value).map((k) => (
              <div key={k} className={cn("min-w-0", isArray ? "relative pl-5 border-l-2 border-gray-200 ml-1" : "w-full")}>
                {isArray && <div className="absolute -left-[5px] top-1 w-2 h-2 rounded-full bg-gray-300" />}
                <JsonFormNode 
                  dataKey={isArray ? `Item ${Number(k) + 1}` : k} 
                  value={value[k]} 
                  path={[...path, k]} 
                  onChange={onChange} 
                  _color={_color} 
                  expandSignal={expandSignal}
                  collapseSignal={collapseSignal}
                  readOnly={readOnly}
                  onDelete={!readOnly ? () => {
                    onChange(path, deleteDeepKey({ _: value }, ["_", k])._)
                  } : undefined}
                  onClone={!readOnly && dataKey !== "simCardRule" ? () => {
                  const valueToClone = value[k]
                  if (isArray) {
                    const newArray = [...value]
                    newArray.splice(Number(k) + 1, 0, valueToClone)
                    onChange(path, newArray)
                  } else {
                    Swal.fire({
                      title: "Clonar Propiedad",
                      input: "text",
                      inputPlaceholder: "Nombre de la copia",
                      inputValue: `${k}_copy`,
                      showCancelButton: true,
                      confirmButtonText: "Clonar",
                      cancelButtonText: "Cancelar",
                      ...getIOSSwalConfig(_color)
                    }).then((res) => {
                      if (res.value) {
                        if (value[res.value] !== undefined) {
                          Swal.fire({
                            title: "Error",
                            text: "La propiedad ya existe",
                            icon: "error",
                            ...getIOSSwalConfig(_color)
                          })
                        } else {
                          const newObj = { ...value, [res.value]: valueToClone }
                          onChange(path, newObj)
                        }
                      }
                    })
                  }
                } : undefined}
              />
            </div>
          ))}
        </div>
        )}
      </div>
    )
  }

  return null
}

interface GenericJsonFormProps {
  jsonString: string
  onChangeString: (newString: string) => void
  readOnly?: boolean
}

const GenericJsonForm: React.FC<GenericJsonFormProps> = ({ jsonString, onChangeString, readOnly }) => {
  const { _color } = usePreRender()
  const [expandSignal, setExpandSignal] = React.useState(0)
  const [collapseSignal, setCollapseSignal] = React.useState(0)
  const [isAllCollapsed, setIsAllCollapsed] = React.useState(false)
  const [searchQuery, setSearchQuery] = React.useState("")

  let parsed: any = null
  try {
    parsed = JSON.parse(jsonString)
  } catch (e) {
    return (
      <div className="flex flex-col items-center justify-center h-full p-8 text-center bg-red-50/50">
        <div className="w-12 h-12 bg-red-100 text-red-500 rounded-full flex items-center justify-center mb-3">
          <span className="text-xl font-bold">!</span>
        </div>
        <h3 className="text-red-800 font-bold mb-1">JSON Inválido</h3>
        <p className="text-sm text-red-600">Corrige el formato en la vista JSON antes de poder usar el formulario visual.</p>
      </div>
    )
  }

  const handleChange = (path: string[], val: any) => {
    const updated = setDeepValue(parsed, path, val)
    onChangeString(JSON.stringify(updated, null, 2))
  }

  if (typeof parsed !== "object" || parsed === null) {
    return <div className="text-sm text-gray-500 p-4">La raíz debe ser un objeto o arreglo.</div>
  }

  const q = searchQuery.toLowerCase().trim()
  const filteredKeys = Object.keys(parsed).filter(k =>
    !q || k.toLowerCase().includes(q) || (FIELD_LABELS[k] || "").toLowerCase().includes(q)
  )

  return (
    <div className="w-full h-full p-4 lg:p-6 bg-white overflow-y-auto">
      <div className="max-w-[1400px] w-full mx-auto mb-4 flex items-center gap-3">
        {/* Search */}
        <div className="flex-1 flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-full px-4 py-2 shadow-sm transition-all focus-within:bg-white focus-within:border-gray-300 focus-within:shadow-md">
          <Search size={15} className="text-gray-400 shrink-0" />
          <input
            type="text"
            placeholder="Buscar propiedad..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            className="flex-1 text-[13px] text-gray-700 bg-transparent outline-none border-none focus:ring-0 placeholder:text-gray-400 appearance-none p-0 m-0"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery("")}
              className="w-4 h-4 flex items-center justify-center rounded-full bg-gray-300 hover:bg-gray-400 text-white text-[10px] font-bold transition-colors shrink-0"
            >
              ×
            </button>
          )}
        </div>
        {/* Expand/collapse */}
        <button
          onClick={() => {
            if (isAllCollapsed) {
              setExpandSignal(s => s + 1)
              setIsAllCollapsed(false)
            } else {
              setCollapseSignal(s => s + 1)
              setIsAllCollapsed(true)
            }
          }}
          className="flex items-center gap-1.5 text-[11px] font-bold text-gray-500 hover:text-blue-600 uppercase tracking-wider transition-colors bg-white px-3 py-2 rounded-md border border-gray-200 shadow-sm shrink-0"
        >
          {isAllCollapsed ? (
            <><ChevronsUpDown size={14} /> Expandir</>
          ) : (
            <><ChevronsDownUp size={14} /> Contraer</>
          )}
        </button>
      </div>
      <div className="max-w-[1400px] w-full mx-auto space-y-3">
        {filteredKeys.length === 0 ? (
          <div className="text-sm text-gray-400 text-center py-8">Sin resultados para "{searchQuery}"</div>
        ) : (
          filteredKeys.map((k) => (
            <JsonFormNode key={k} dataKey={k} value={parsed[k]} path={[k]} onChange={handleChange} _color={_color} expandSignal={expandSignal} collapseSignal={collapseSignal} readOnly={readOnly} />
          ))
        )}
      </div>
    </div>
  )
}

export default GenericJsonForm
